import React, { useState } from 'react'
import { ActivityMonitorIcon, Div, StatusIndicator, StyledChip } from './styles'
import { Box, Tooltip, useMediaQuery, IconButton, Menu, Divider } from '@mui/material'
import { InstitutionDropdown, StyledMenuItem, StyledGroupName } from '../AdminMenuItems/styles'
import { useTheme } from '@mui/material/styles'
import AdminMenuItems from '../AdminMenuItems'
import ReportMenuItems from '../ReportMenuItems'
import AccountDropdown from '../AccountDropdown'
import RoleDropdown from '../RoleDropdown'
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined'
import { BotMessageSquare, School, Settings } from 'lucide-react'
import ROUTES from '../../../consts/paths'
import { useNavigateToUrl } from '../../../hooks/useNavigateToUrl'
import { env } from '../../../env'
import { useAllowedPath } from '../../../hooks/useAllowedPath'
import { blue, grey } from '../../../colors'
import { ROLES, ReportRoles } from '../../../consts/roles'
import { isViewerFamilyRole } from '../../../helpers/roleHelpers'
import { useSetupTour } from '../../../features/SetupWizardFeature/SetupWizardProvider'
import { useUserProfile } from '../../../hooks/useUserProfile'
import { hasActionableSurveys, useStudentDistributions } from '../../../hooks/useStudentDistributions'
import {
  adminInstitutionMenuList,
  adminSettingsMenuList,
  type MenuItemProps
} from '../../../consts/TopbarMenuItems'

const TabMenu: React.FC = () => {
  const theme = useTheme()
  const navigateToURL = useNavigateToUrl()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const showActivityMonitor = useAllowedPath(ROUTES.TASKS)
  const environment = env.REACT_APP_AWS_ENVIRONMENT
  const environmentName = env.REACT_APP_AWS_ENVIRONMENT?.split('-')[0]
  const tenant = localStorage.getItem('tenantKey')
  const chipText = `${environmentName}: ${tenant}`
  const showEnvironmentFlag = environment !== 'prod-classranked'

  // Permission checks (for menu items only)
  const canAccessInstitutionalSettings = useAllowedPath(ROUTES.INSTITUTION_SETTINGS)
  const canAccessAttributes = useAllowedPath(ROUTES.ATTRIBUTES)
  const canAccessAcademicUnits = useAllowedPath(ROUTES.ACADEMIC_UNITS)
  const canAccessTerms = useAllowedPath(ROUTES.TERMS)
  const canAccessFacultyAndStaff = useAllowedPath(ROUTES.FACULTY_AND_STAFF)
  const canAccessReportingHierarchy = useAllowedPath(ROUTES.REPORTING_HIERARCHY)
  const canAccessEmail = useAllowedPath(ROUTES.EMAIL)
  const canAccessSisSyncLogs = useAllowedPath(ROUTES.SIS_SYNC_LOGS)

  // Active state checks
  const canAccessCourseCatalog = useAllowedPath(ROUTES.COURSE_CATALOG)
  const currentPath = window.location.pathname
  const SettingsActiveLinks = [ROUTES.ATTRIBUTES, ROUTES.ACADEMIC_UNITS, ROUTES.INSTITUTION_SETTINGS, ROUTES.EMAIL, ROUTES.SIS_SYNC_LOGS]
  const isSettingsActive = SettingsActiveLinks.some(link => currentPath.includes(link))
  const isTaskActive = currentPath === ROUTES.TASKS
  const InstitutionDropdownActive = [ROUTES.TERMS, ROUTES.FACULTY_AND_STAFF, ROUTES.COURSE_CATALOG, ROUTES.REPORTING_HIERARCHY]
  const isInstitutionActive = InstitutionDropdownActive.some(link => currentPath.includes(link))

  const [chartAnchorEl, setChartAnchorEl] = useState<null | HTMLElement>(null)
  const chartOpen = Boolean(chartAnchorEl)

  const [settingsAnchorEl, setSettingsAnchorEl] = useState<null | HTMLElement>(null)
  const settingsOpen = Boolean(settingsAnchorEl)
  const { data: userProfile } = useUserProfile()
  const currentUserRoles = userProfile?.roles ?? []
  const currentRoleId = localStorage.getItem('currentRoleId')
  const currentRole = currentUserRoles.find(role => role.roleId === currentRoleId)
  const { isWizardOpen, setIsWizardOpen } = useSetupTour()
  // Page 1 only: this surface needs `actionableCount`, which describes the whole result set and
  // is present on every page. Shares its cache entry with the dashboard, so the two do not both
  // fetch page 1.
  const { actionableCount } = useStudentDistributions(
    1,
    currentRole?.roleName === ROLES.PRIMARY_INSTRUCTOR
  )

  const canAccessByRoute: Record<string, boolean> = {
    [ROUTES.TERMS]: canAccessTerms,
    [ROUTES.FACULTY_AND_STAFF]: canAccessFacultyAndStaff,
    [ROUTES.COURSE_CATALOG]: canAccessCourseCatalog,
    [ROUTES.REPORTING_HIERARCHY]: canAccessReportingHierarchy,
    [ROUTES.ATTRIBUTES]: canAccessAttributes,
    [ROUTES.ACADEMIC_UNITS]: canAccessAcademicUnits,
    [ROUTES.INSTITUTION_SETTINGS]: canAccessInstitutionalSettings,
    [ROUTES.EMAIL]: canAccessEmail,
    [ROUTES.SIS_SYNC_LOGS]: canAccessSisSyncLogs
  }

  const institutionMenuItems = adminInstitutionMenuList.reduce<MenuItemProps[]>((acc, item) => {
    const links = item.links.filter((link) => canAccessByRoute[link.link] ?? false)
    if (links.length > 0) {
      acc.push({ ...item, links })
    }
    return acc
  }, [])

  const settingsMenuItems = adminSettingsMenuList.flatMap((item) =>
    item.links.filter((link) => canAccessByRoute[link.link] ?? false)
  )

  // Show institution icon only for specific roles
  const allowedInstitutionRoles = [
    ROLES.PRIMARY_INSTRUCTOR,
    ROLES.EDITOR,
    ReportRoles.Owner
  ]
  const shouldShowInstitutionIcon = currentRole && (
    allowedInstitutionRoles.includes(currentRole.roleName as ROLES | ReportRoles) ||
    isViewerFamilyRole(currentRole.roleName)
  )

  // Show settings icon if there are any settings menu items (permission-based)
  const hasSettingsMenu = settingsMenuItems.length > 0
  const shouldShowSettingsIcon = hasSettingsMenu

  // Home and Take Surveys buttons (still role-based, as they are not menu icons)
  // Server-computed (CR-293). This used to be `.some()` over the whole client-side array, which
  // pagination would have silently narrowed to one page — and since this button *is* the route to
  // a user's surveys, a wrong answer here removes their only way in. `actionableCount` is computed
  // over the entire result set; the predicate, including what to do when it is unknown, lives with
  // the hook so this surface and any future one cannot disagree about it.
  const hasActiveSurveys = hasActionableSurveys(actionableCount)
  const shouldShowTakeSurveys = currentRole?.roleName === ROLES.PRIMARY_INSTRUCTOR && hasActiveSurveys
  const shouldShowHomeButton = currentRole?.roleName === ROLES.PRIMARY_INSTRUCTOR ||
    currentRole?.roleName === ROLES.STUDENT

  const shouldShowSetupWizardIcon = hasSettingsMenu && (currentRole?.roleName === ROLES.ACCOUNT_OWNER || currentRole?.roleName === ROLES.SYSTEM_MANAGER)
  const shouldShowActivityMonitor = showActivityMonitor && hasSettingsMenu && (
    currentRole?.roleName === ROLES.ACCOUNT_OWNER ||
    currentRole?.roleName === ROLES.SYSTEM_MANAGER ||
    currentRole?.roleName === ReportRoles.Owner ||
    currentRole?.roleName === ROLES.EDITOR
  )

  const handleHomeClick = (): void => {
    if (!currentRole) return

    switch (currentRole.roleName) {
      case ROLES.ACADEMIC_UNIT_OWNER:
      case ReportRoles.Owner:
      case ReportRoles.Viewer:
        // Navigate to Admin Dashboard (current Admin Dashboard until dedicated homepage is built)
        navigateToURL({} as any, ROUTES.ADMIN_VIEWER_DASHBOARD, false)
        break
      case ROLES.PRIMARY_INSTRUCTOR:
        // Navigate to Instructor Dashboard
        navigateToURL({} as any, ROUTES.INSTRUCTOR_SURVEYS, false)
        break
      case ROLES.STUDENT:
        // Navigate to Surveys Due page
        navigateToURL({} as any, ROUTES.STUDENT_SURVEYS, false)
        break
      default:
        break
    }
  }

  // Event handlers
  const handleInstitutionClick = (event: React.MouseEvent<HTMLElement>): void => {
    setChartAnchorEl(event.currentTarget)
  }

  const handleInstitutionClose = (): void => {
    setChartAnchorEl(null)
  }

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>): void => {
    setSettingsAnchorEl(event.currentTarget)
  }

  const handleSettingsClose = (): void => {
    setSettingsAnchorEl(null)
  }

  return (
    <Div isMobile={isMobile}>
      <Box display={'flex'} gap={1}>
        <ReportMenuItems />
        <AdminMenuItems />
        {shouldShowHomeButton && (
          <InstitutionDropdown
            isMobile={isMobile}
            isTablet={false}
            active={window.location.pathname === ROUTES.INSTRUCTOR_SURVEYS || window.location.pathname === ROUTES.STUDENT_SURVEYS || window.location.pathname === ROUTES.ADMIN_VIEWER_DASHBOARD}
            onClick={handleHomeClick}
          >
            Home
          </InstitutionDropdown>
        )}
        {shouldShowTakeSurveys && (
          <InstitutionDropdown
            isMobile={isMobile}
            isTablet={false}
            active={window.location.pathname === ROUTES.STUDENT_SURVEYS}
            onClick={() => { navigateToURL({} as any, ROUTES.STUDENT_SURVEYS, false) }}
          >
            Take Surveys
          </InstitutionDropdown>
        )}
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        {shouldShowSettingsIcon && (
          <>
            <Tooltip title="Settings">
              <IconButton
                size="small"
                onClick={handleSettingsClick}
                sx={{
                  color: isSettingsActive || settingsOpen ? blue[500] : grey[50],
                  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
                  borderRadius: `${theme.spacing(3)}`,
                  '&:hover': {
                    color: blue[500],
                    backgroundColor: blue[100]
                  }
                }}
              >
                <Settings size={24} />
              </IconButton>
            </Tooltip>
            {shouldShowSetupWizardIcon && (
              <Tooltip title="Launch Setup Wizard">
                <IconButton
                  size="small"
                  onClick={() => { setIsWizardOpen(true) }}
                  sx={{
                    color: isWizardOpen ? blue[500] : grey[50],
                    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
                    borderRadius: `${theme.spacing(3)}`,
                    '&:hover': {
                      color: blue[500],
                      backgroundColor: blue[100]
                    }
                  }}
                >
                  <BotMessageSquare size={24} />
                </IconButton>
              </Tooltip>
            )}
            {shouldShowActivityMonitor && (
              <Tooltip title='Go to Activity Monitor'>
                <ActivityMonitorIcon
                  sx={isTaskActive
                    ? {
                        color: blue[500],
                        backgroundColor: blue[100]
                      }
                    : {}
                  }
                  onClick={(e) => { navigateToURL(e, ROUTES.TASKS, false) }}
                >
                  <MonitorHeartOutlinedIcon />
                </ActivityMonitorIcon>
              </Tooltip>
            )}
            <Menu
              anchorEl={settingsAnchorEl}
              open={settingsOpen}
              onClose={handleSettingsClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              sx={{ mt: 1 }}
            >
              {settingsMenuItems.map((item) => (
                <StyledMenuItem
                  key={item.link}
                  active={isSettingsActive && window.location.pathname === item.link}
                  onClick={(e) => { navigateToURL(e as any, item.link, false); handleSettingsClose() }}
                >
                  {item.label}
                </StyledMenuItem>
              ))}
            </Menu>
          </>
        )}
        {shouldShowInstitutionIcon && (
          <>
            <Tooltip title="Institution">
              <IconButton
                size="small"
                onClick={handleInstitutionClick}
                sx={{
                  color: chartOpen || isInstitutionActive ? blue[500] : grey[50],
                  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
                  borderRadius: `${theme.spacing(3)}`,
                  '&:hover': {
                    color: blue[500],
                    backgroundColor: blue[100]
                  }
                }}
              >
                <School size={24} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={chartAnchorEl}
              open={chartOpen}
              onClose={handleInstitutionClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              sx={{ mt: 1 }}
            >
              {institutionMenuItems.map((group, groupIndex) => (
                <React.Fragment key={groupIndex}>
                  {group.group && (
                    <StyledGroupName tabIndex={1}>
                      {group.group}
                    </StyledGroupName>
                  )}
                  {group.links.map((item) => (
                    <StyledMenuItem
                      key={item.link}
                      active={currentPath === item.link}
                      onClick={(e) => { navigateToURL(e as any, item.link, false); handleInstitutionClose() }}
                    >
                      {item.label}
                    </StyledMenuItem>
                  ))}
                  {group.group && groupIndex < institutionMenuItems.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Menu>
          </>
        )}
        <RoleDropdown />
        {showEnvironmentFlag && (
          <StyledChip status={environment} label={
            <Box display='flex' alignItems='center' gap={1}>
              <StatusIndicator status={environment} /> {chipText}
            </Box>
          } />
        )}
        <AccountDropdown />
      </Box>
    </Div>
  )
}

export default TabMenu
